import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronDown } from "lucide-react";
import Shell from "@/components/Shell";

const UPDATED = "June 2026";

export const PrivacyPolicy = () => (
  <LegalShell title="Privacy Policy" testid="privacy-policy">
    <p><strong>Signal State ID is built privacy-first.</strong> It is a personal, self-reflection tool — not a medical or diagnostic service. This policy explains, in plain language, what we hold and what we don't.</p>

    <H>What we store</H>
    <ul>
      <li>The check-ins you choose to record (your reported signal, the context and time you selected, the response suggested, and any feedback you give).</li>
      <li>If you sign in: your email address, used only to send your sign-in link and to keep your history across devices.</li>
      <li>Basic technical data needed to run the app (an anonymous device identifier, and billing status if you subscribe to Plus).</li>
    </ul>

    <H>What we do NOT do</H>
    <ul>
      <li>We do not sell, rent, or share your personal data or check-in content with third parties for advertising.</li>
      <li>We do not build advertising profiles, and we do not use your private check-in content to train external models.</li>
      <li>Our team's admin tools show only account and billing metadata and aggregate counts — never the content of any individual person's check-ins.</li>
    </ul>

    <H>Payments</H>
    <p>Subscriptions are processed by Stripe. Your card details are handled by Stripe and never stored on our servers. We keep only the subscription status needed to give you access.</p>

    <H>Your control</H>
    <ul>
      <li>You can export your Pattern Review at any time from the app.</li>
      <li>You can delete individual check-ins, and you can permanently delete your data from Settings.</li>
      <li>You can use most of the app without an account at all.</li>
    </ul>

    <H>Contact</H>
    <p>Questions about your data? Email <a href="mailto:support@signalstateid.com">support@signalstateid.com</a>.</p>
  </LegalShell>
);

export const Terms = () => (
  <LegalShell title="Terms of Use" testid="terms">
    <div className="rounded-xl bg-[#F5F3ED] p-4 text-[13.5px] leading-relaxed text-[#474747]" data-testid="nonclinical-disclaimer">
      <strong>Important — not medical advice.</strong> Signal State ID is a self-reflection and educational tool.
      It is <strong>not</strong> a medical device, does not diagnose or treat any condition, and is not a
      substitute for professional medical, psychological, or crisis care. If you are in distress or may be
      unsafe, contact your local emergency services or a crisis line immediately.
    </div>

    <H>Using Signal State ID</H>
    <p>Signal State ID helps you notice your current state and choose a small, realistic next step. Everything it shows is observational — based only on what you report — and reflects patterns, not certainties or clinical findings.</p>

    <H>Your account</H>
    <p>Sign-in is passwordless. Keep access to your email secure, as anyone with your inbox can request a sign-in link. You're responsible for the accuracy of what you record.</p>

    <H>Plus subscription</H>
    <p>Signal State ID Plus is billed through Stripe. Trials, renewal dates, and cancellation options are shown in the app under Manage subscription. You can cancel at any time and keep access until the end of your paid period.</p>

    <H>Acceptable use</H>
    <p>Please don't misuse the service, attempt to disrupt it, or rely on it as a diagnosis or a replacement for professional help.</p>

    <H>Changes</H>
    <p>We may update these terms as the product evolves; material changes will be reflected here with a new date.</p>

    <H>Contact</H>
    <p>Questions? Email <a href="mailto:support@signalstateid.com">support@signalstateid.com</a>.</p>
  </LegalShell>
);

export const FAQ_ITEMS = [
  {
    q: "What is Signal State ID?",
    a: "It's a private, non-clinical self-check-in tool. In about 60 seconds it helps you name how you're operating right now and choose one realistic next move. It's built for everyday moments — feeling overloaded, scattered, depleted, on edge, or stuck — and it hands you back to your own judgment rather than labeling you.",
  },
  {
    q: "Is Signal State ID therapy or medical advice?",
    a: "No. It is not therapy, counseling, medical advice, or a diagnostic tool, and it does not treat any condition. It's a self-reflection aid. If you're struggling with your mental health, please reach out to a qualified professional — Signal State ID can sit alongside that support, but it never replaces it.",
  },
  {
    q: "Is my data private? Who can see my check-ins?",
    a: "Privacy is the default. Your check-in content is yours — we never sell or share it, never build advertising profiles, and never use your private entries to train outside AI models. Our own admin tools show only account and billing info plus anonymous aggregate counts, never the content of any individual's check-ins. You can use most of the app without an account, export your history, or delete your data at any time.",
  },
  {
    q: "How does it work?",
    a: "You answer three quick questions — what you're feeling, the demand in front of you, and how much time you have. The result clearly separates your state (how you're operating) from your response (a suggested mode plus one short, doable step). Optional feedback afterward — did you try it, did it help — quietly improves what the app can reflect back to you.",
  },
  {
    q: "What is Drift Radar, and does it use AI to analyze me?",
    a: "Drift Radar highlights patterns drawn only from your own past check-ins, using deterministic, rules-based logic — not generative AI guessing about you. It only speaks up once it has enough of your data to be honest (shown as an Earned Intelligence stage), and it always frames observations as patterns you can accept or dismiss, never as diagnoses or predictions.",
  },
  {
    q: "Do I need to create an account?",
    a: "No — you can use the core check-in anonymously on your device. Creating a passwordless account with your email is optional; it simply keeps your history across devices and unlocks Plus features. Sign-in uses a secure magic link sent to your inbox, so there's no password to manage.",
  },
  {
    q: "How much does it cost? Is there a free trial?",
    a: "The core check-in is free. Plus is $9.98 per month or $83.83 per year, and every new subscription starts with a 21-day free trial. You can cancel any time and keep access until the end of your paid period. Payments are handled securely on the website by Stripe.",
  },
  {
    q: "How do I cancel or manage my subscription?",
    a: "Open Settings, then Manage subscription in the app. There you can switch between monthly and annual, see your renewal or trial-end date, and cancel. Cancelling stops future billing and keeps your access until the current period ends — no email or phone call needed.",
  },
  {
    q: "Does it work offline? Can I install it on my phone?",
    a: "Yes. Signal State ID is a Progressive Web App — you can add it to your home screen on iPhone or Android and complete check-ins even without a connection; they sync when you're back online. No app store required.",
  },
  {
    q: "What should I do in a crisis or emergency?",
    a: "Signal State ID is not a crisis service. If you or someone else may be unsafe, or you're in serious distress, contact your local emergency number or a crisis line right away, or reach out to a mental-health professional. Please don't rely on this app for urgent or life-threatening situations.",
  },
];

export const FAQ = () => (
  <LegalShell title="Frequently asked questions" testid="faq">
    <p>Common questions about what Signal State ID is, how it protects your privacy, and how it works. Still unsure? Email <a href="mailto:support@signalstateid.com">support@signalstateid.com</a>.</p>
    <div className="mt-2 flex flex-col gap-3" data-testid="faq-list">
      {FAQ_ITEMS.map((item, i) => (
        <details key={i} className="ss-faq group rounded-xl border border-[#EBE9E4] bg-white/60 p-4" data-testid={`faq-item-${i}`}>
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-[15px] font-semibold text-[#1A1A1A] ss-focus">
            {item.q}
            <ChevronDown size={18} strokeWidth={1.75} className="shrink-0 text-[#8a6d10] transition-transform group-open:rotate-180" />
          </summary>
          <p className="mt-3 text-[14px] leading-relaxed text-[#474747]">{item.a}</p>
        </details>
      ))}
    </div>
    <style>{`.ss-faq summary::-webkit-details-marker{display:none}`}</style>
  </LegalShell>
);

function LegalShell({ title, testid, children }) {
  const nav = useNavigate();
  return (
    <Shell>
      <div className="px-6 pt-12 pb-24" data-testid={testid}>
        <button data-testid="legal-back" onClick={() => (window.history.length > 1 ? nav(-1) : nav("/"))}
          className="inline-flex items-center gap-1 text-[13px] text-[#6B6B6B] hover:text-[#1A1A1A] ss-focus -ml-1">
          <ArrowLeft size={18} strokeWidth={1.5} /> Back
        </button>
        <h1 className="mt-8 font-serif-display text-4xl leading-tight">{title}</h1>
        <p className="mt-2 text-[12.5px] text-[#9a9a94]">Last updated {UPDATED}</p>
        <div className="ss-legal mt-6 space-y-4 text-[14.5px] leading-relaxed text-[#474747]">
          <style>{`.ss-legal ul{list-style:disc;padding-left:1.25rem;display:flex;flex-direction:column;gap:.4rem}.ss-legal a{color:#8a6d10;text-decoration:underline}.ss-legal strong{color:#1A1A1A}`}</style>
          {children}
        </div>
      </div>
    </Shell>
  );
}

const H = ({ children }) => (
  <h2 className="pt-2 text-[15px] font-semibold text-[#1A1A1A]">{children}</h2>
);
